import { CircularProgress, Typography } from '@mui/material';
import AlertError from 'components/forms/AlertError';
import { getRoutePath } from 'config/routes';
import { unnamed } from 'features/aiWriter/utils/unnamed';
import FormattedMessage from 'features/i18n/FormattedMessage';
import { logWarning } from 'features/logging/logError';
import { FieldRenderer } from 'features/modular-workflow/runner/fields/FieldRenderer';
import { RunnerError } from 'features/modular-workflow/runner/getRunnerErrorsFromResponse';
import { hasStepEditableFields } from 'features/modular-workflow/runner/hasStepEditableFields';
import { isStepInProgress } from 'features/modular-workflow/runner/isStepInProgress';
import { InputRenderer } from 'features/modular-workflow/runner/settings/InputRenderer';
import { StepInProgressState } from 'features/modular-workflow/runner/StepInProgressState';
import {
  RunnerModularWorkflowStep,
  RunnerModularWorkflowStepInput
} from 'features/modular-workflow/runner/types-runner';
import { useCalculateVisibleSteps } from 'features/modular-workflow/runner/useCalculateVisibleSteps';
import {
  useModularWorkflowRunnerStore,
  useRunningWorkflowSelectedStep
} from 'features/modular-workflow/runner/useModularWorkflowRunnerStore';
import { useGetModulareWorkflowStepStructureByReference } from 'features/modular-workflow/useModulareWorkflowStructureStore';
import { CommonWorkflowContainer } from 'features/workflow/CommonWorkflowContainer';
import { KeyboardEvent, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { isInputSubmitAttempt } from 'utils/isInputSubmitAttempt';
import { assertNonNullable } from 'utils/typescript/nonNullable';

type Props = {
  step: RunnerModularWorkflowStep;
  stepIndex: number;
  stepErrorList: RunnerError[];
  onNextStep: (updatedStep: RunnerModularWorkflowStep) => Promise<void>;
  onBackStep: () => Promise<void>;
};

export const StepContainer = ({
  step,
  stepIndex,
  stepErrorList,
  onNextStep,
  onBackStep
}: Props) => {
  // We create a local state with current values to enable "save changes only on confirm click"
  const [inputValues, setInputValues] = useState<
    Record<RunnerModularWorkflowStepInput['reference'], RunnerModularWorkflowStepInput['value']>
  >({});
  const [inputValidationState, setInputValidationState] = useState<
    Record<RunnerModularWorkflowStepInput['reference'], boolean>
  >(() => {
    const valueValidationObject: Record<RunnerModularWorkflowStepInput['reference'], boolean> = {};

    step.inputs.forEach(input => {
      // If the input is required and doesn't have a default value, it's invalid by default
      valueValidationObject[input.reference] = input.is_required && !input.value ? false : true;
    });

    return valueValidationObject;
  });
  const getStepStructure = useGetModulareWorkflowStepStructureByReference();
  const runningWorkflowStep = useRunningWorkflowSelectedStep();

  const workflow = useModularWorkflowRunnerStore(state => state.workflow);
  const resetWorkflow = useModularWorkflowRunnerStore(state => state.reset);

  const [isProcessingStep, setIsProcessingStep] = useState(false);

  const isCurrentStepInProgress = isStepInProgress(runningWorkflowStep);
  const hasCurrentStepFailed = runningWorkflowStep?.status === 'failed';
  const hasInvalidValues = Object.values(inputValidationState).some(isValid => !isValid);

  const [currentVisibleStepIndex, amountVisibleSteps] = useCalculateVisibleSteps(stepIndex);

  const isBackButtonDisabled = isProcessingStep || currentVisibleStepIndex === 0;
  const isNextButtonDisabled = isProcessingStep || hasCurrentStepFailed || hasInvalidValues;

  const hasEditableFields = useMemo(() => hasStepEditableFields(step), [step]);

  assertNonNullable(workflow, 'workflow must be loaded first');

  const workflowName = workflow.name.length > 0 ? workflow.name : unnamed;

  // Prefill local state object with current values
  useEffect(() => {
    const valueStateObject: typeof inputValues = {};
    step.inputs.forEach(input => {
      valueStateObject[input.reference] = input.value;
    });

    setInputValues(valueStateObject);

    // On unmount, reset the input values
    return () => {
      setInputValues({});
      setInputValidationState({});
    };
  }, [step, step.inputs]);

  const handleInputChange = (
    input: RunnerModularWorkflowStepInput,
    value: RunnerModularWorkflowStep['inputs'][number]['value']
  ) => {
    if (inputValues[input.reference] === value) {
      return;
    }

    setInputValues({
      ...inputValues,
      [input.reference]: value
    });
  };

  const handleInputValidationChange = (inputRef: string, isValid: boolean) => {
    setInputValidationState({
      ...inputValidationState,
      [inputRef]: isValid
    });
  };

  const handleNextClick = async () => {
    // Copy all gathered input changes to the current step
    const updatedStep = { ...step };
    updatedStep.inputs.forEach(input => {
      if (!(input.reference in inputValues)) {
        logWarning(new Error('Runner: Failed to find matching input'), 'modular_workflow', {
          workflowName: workflow.name,
          workflowId: workflow.related_workflow_id,
          runningWorkflowId: workflow.id,
          inputReference: input.reference,
          inputValues
        });
        return;
      }

      input.value = inputValues[input.reference];
    });

    setIsProcessingStep(true);
    await onNextStep(updatedStep);
    setIsProcessingStep(false);
  };

  const handleBackClick = async () => {
    setIsProcessingStep(true);
    await onBackStep();
    setIsProcessingStep(false);
  };

  const handleEnterKeyPress = (event: KeyboardEvent) => {
    if (isNextButtonDisabled) {
      return;
    }

    if (!isInputSubmitAttempt(event)) {
      return;
    }

    event.preventDefault();
    handleNextClick();
  };

  const stepStructure = getStepStructure(step.reference);
  if (!stepStructure) {
    return null;
  }

  if (isCurrentStepInProgress) {
    return (
      <CommonWorkflowContainer
        stepCount={amountVisibleSteps}
        activeStep={currentVisibleStepIndex}
        hasBackButton={false}
        nextButtonProps={{ disabled: true, startIcon: <CircularProgress size={20} /> }}
      >
        <Typography variant="h6">{workflowName}</Typography>

        <StepInProgressState />
      </CommonWorkflowContainer>
    );
  }

  return (
    <CommonWorkflowContainer
      stepCount={amountVisibleSteps}
      activeStep={currentVisibleStepIndex}
      hasNextButton={hasEditableFields}
      hasBackButton={currentVisibleStepIndex !== 0}
      nextButtonProps={{
        onClick: handleNextClick,
        disabled: isNextButtonDisabled,
        startIcon: isProcessingStep ? <CircularProgress size={20} /> : undefined
      }}
      backButtonProps={{
        onClick: handleBackClick,
        disabled: isBackButtonDisabled
      }}
    >
      <Typography variant="h6">{workflowName}</Typography>

      <Root>
        {step.inputs
          .filter(input => input.is_editable)
          .map((input, index) => {
            if (input.is_field) {
              return (
                <FieldRenderer
                  key={`${input.reference}-${step.order}`}
                  input={input}
                  step={step}
                  errorList={stepErrorList.filter(error => error.inputIndex === index)}
                  onChange={handleInputChange}
                  onValidationChange={handleInputValidationChange}
                  onKeyDown={handleEnterKeyPress}
                />
              );
            }

            return (
              <InputRenderer
                key={`${input.reference}-${step.order}`}
                input={input}
                step={step}
                stepSettings={stepStructure.settings}
                errorList={stepErrorList.filter(error => error.inputIndex === index)}
                onChange={handleInputChange}
                onValidationChange={handleInputValidationChange}
                inputValues={inputValues}
              />
            );
          })}

        {hasCurrentStepFailed && (
          <AlertError>
            <FormattedMessage
              id="modular_workflow.runner.error_general"
              values={{
                restart: (chunks: string) => (
                  <a
                    onClick={() => {
                      resetWorkflow();
                      location.replace(
                        getRoutePath('modularWorkflowRun', { workflowId: workflow.id })
                      );
                    }}
                    style={{ cursor: 'pointer' }}
                  >
                    {chunks}
                  </a>
                )
              }}
            />
          </AlertError>
        )}
      </Root>
    </CommonWorkflowContainer>
  );
};

const Root = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings.medium};
  padding: ${({ theme }) => theme.spacings.small};
`;
